import i18n from "components/i18n/i18n";
import { Option } from "components/input/select/options";
import optionsTemplate from "components/input/select/options.hbs";
import { FocusManager } from "components/pages/focusManager";
import { DoubleListSelectElement } from "./doubleListElement";
import template from "./doubleListLeftSideElement.hbs";
import "./doubleListLeftSideElement.scss";

export class DoubleListLeftSideElement extends HTMLElement {

  private _doubleListSelect: DoubleListSelectElement;
  private _options: Option[];

  private filterInput: HTMLInputElement;
  private leftSelect: HTMLSelectElement;

  private filteredOptions: Option[];

  constructor() {
    super();
    this.innerHTML = template({});
    this.filterInput = this.querySelector("input");
    this.filterInput.placeholder = this.getPlaceholderFilterInput();
    this.leftSelect = this.querySelector("select");
    this.bindEvents();
  }

  private bindEvents() {
    this.filterInput.addEventListener("focus", (evt) => {
      evt.stopPropagation();
    });
    this.filterInput.addEventListener("change", (evt) => {
      evt.stopPropagation();
    });
    this.filterInput.addEventListener("keyup", (evt) => {
      const hasChangeFilteredOptions = this.setFilterOptions();
      this.render(hasChangeFilteredOptions);
    });
    this.addEventTabOnEnter(this.filterInput);

    this.leftSelect.addEventListener("change", (evt) => {
      evt.stopPropagation();
    });
    this.leftSelect.addEventListener("dblclick", (evt) => {
      if (this._doubleListSelect != null) {
        this._doubleListSelect.moveToRight();
      }
    });
    this.addEventTabOnEnter(this.leftSelect);
  }

  private addEventTabOnEnter(element: HTMLElement) {
    if (this.hasAttribute("tabOnEnter")) {
      element.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.stopPropagation();
          e.preventDefault();
          FocusManager.focusNext();
        }
      });
    }
  }

  private getPlaceholderFilterInput(): string {
    let placeholder = i18n.t("base.filter.action") + "...";
    if (this._doubleListSelect != null && this._doubleListSelect.optionsInputFilter.minNumsCharsToFilter != 0) {
      placeholder += " (" + this._doubleListSelect.optionsInputFilter.minNumsCharsToFilter + " chars.)";
    }
    return placeholder;
  }

  private setFilterOptions(): boolean {
    let value = this.filterInput.value;
    const oldSizeFilteredOptions = this.filteredOptions?.length ?? this._options.length;

    if (this.isFiltering()) {
      this.filteredOptions = this._options.filter((o) => {
        if (this._doubleListSelect.optionsInputFilter.useWildcars) {
          return this.filteringWithWildcars(o, value);
        } else {
          return this.filteringWithoutWildcars(o, value);
        }
      });
    } else {
      this.filteredOptions = [...this._options];
    }

    return (oldSizeFilteredOptions != this.filteredOptions.length);
  }

  private filteringWithWildcars(o: Option, value: any): boolean {
    let escapeStrRegExp = value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    let strRegExp = escapeStrRegExp;
    strRegExp = strRegExp.replaceAll(this._doubleListSelect.optionsInputFilter.wildcardChar, '.');
    strRegExp = strRegExp.replaceAll(this._doubleListSelect.optionsInputFilter.wildcardGroupChars, '.*')
    strRegExp = '^' + strRegExp + '$';

    let regExp = null;
    if (this._doubleListSelect.optionsInputFilter.caseSensitive) {
      regExp = new RegExp(strRegExp, 'g');
    } else {
      regExp = new RegExp(strRegExp, 'gi');
    }

    return regExp.test(o.value?.toString()) || regExp.test(o.text?.toString());
  }

  private filteringWithoutWildcars(o: Option, value: any): boolean {
    if (this._doubleListSelect.optionsInputFilter.caseSensitive) {
      return (o.value?.toString().includes(value)
        || o.text?.includes(value));
    } else {
      return (o.value?.toString().toLowerCase().includes(value.toLowerCase())
        || o.text?.toLowerCase().includes(value.toLowerCase()));
    }
  }

  private connectedCallback() {
    this.upgradeProperty();
  }

  private upgradeProperty() {
    for (const key in this) {
      if (this.hasOwnProperty(key)) {
        let value = this[key];
        delete this[key];
        this[key] = value;
      }
    }
  }

  public set doubleListSelect(doubleListSelect) {
    this._doubleListSelect = doubleListSelect;
  }

  public set readOnly(ro: boolean) {
    this.filterInput.disabled = ro;
    this.leftSelect.disabled = ro;
  }

  public set options(options) {
    this._options = options;
    this.setFilterOptions();
    this.render(true);
  }

  public get options() {
    return this._options;
  }

  public get filterOptions() {
    return this.filteredOptions;
  }

  public isFiltering(): boolean {
    let value = this.filterInput.value;
    return (value != null && value !== "" && value.length >= this._doubleListSelect.optionsInputFilter.minNumsCharsToFilter);
  }

  private render(hasChangeFilteredOptions: boolean) {

    this.filterInput.placeholder = this.getPlaceholderFilterInput();

    //Render new values left select, if size options change
    if (hasChangeFilteredOptions) {
      this.leftSelect.innerHTML = "";
      this.leftSelect.innerHTML = optionsTemplate({
        options: this.filteredOptions,
        codeInOptionLabel: this.hasAttribute("codeInOptionLabel"),
      });
    }
  }

  public get selectedOptions() {
    return this.leftSelect.selectedOptions;
  }

  public set disabled(d) {
    this.filterInput.disabled = d;
    this.leftSelect.disabled = d;
  }
}

export function initDoubleListLeftSideWebComponent() {
  window.customElements.define("s6-double-list-left-side", DoubleListLeftSideElement);
}
